/* VÊLES — theme scripts (no dependencies) */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};

  /* ------------------------------------------------------------------ */
  /* Utilities                                                           */
  /* ------------------------------------------------------------------ */

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...options,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || strings.error || 'Error');
      err.data = data;
      throw err;
    }
    return data;
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  let lockCount = 0;
  const lockScroll = () => { lockCount += 1; document.body.classList.add('is-locked'); };
  const unlockScroll = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.body.classList.remove('is-locked'); };

  const storage = {
    get(key) { try { return JSON.parse(localStorage.getItem(key)); } catch (e) { return null; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ } },
  };
  const session = {
    get(key) { try { return JSON.parse(sessionStorage.getItem(key)); } catch (e) { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode */ } },
    remove(key) { try { sessionStorage.removeItem(key); } catch (e) { /* private mode */ } },
  };

  /* ------------------------------------------------------------------ */
  /* Custom select: every <select class="select"> gets a styled dropdown  */
  /* ------------------------------------------------------------------ */

  // The native select stays in the page (hidden) for the form value, validation and the theme
  // code; the visible button + list mirror it and write back to it with a real "change" event.
  const CustomSelect = {
    open: null,

    enhance(select) {
      if (select.closest('.cselect') || select.multiple) return;
      const wrap = document.createElement('div');
      wrap.className = 'cselect';
      select.parentNode.insertBefore(wrap, select);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'select cselect__button';
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      if (select.id) {
        const label = document.querySelector(`label[for="${CSS.escape(select.id)}"]`);
        if (label) {
          if (!label.id) label.id = `${select.id}-label`;
          button.setAttribute('aria-labelledby', `${label.id} ${select.id}-cvalue`);
          label.addEventListener('click', (e) => { e.preventDefault(); button.focus(); });
        }
      }
      const value = document.createElement('span');
      value.className = 'cselect__value';
      if (select.id) value.id = `${select.id}-cvalue`;
      button.append(value);
      const list = document.createElement('ul');
      list.className = 'cselect__list';
      list.setAttribute('role', 'listbox');
      list.hidden = true;
      select.classList.add('cselect__native');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      wrap.append(button, list, select);
      const state = { select, button, list, value, wrap, active: -1 };
      select._cselect = state;

      // Programmatic changes (theme code, browser autofill) keep the button in sync.
      ['value', 'selectedIndex'].forEach((prop) => {
        const desc = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, prop);
        Object.defineProperty(select, prop, {
          configurable: true,
          get() { return desc.get.call(this); },
          set(v) { desc.set.call(this, v); CustomSelect.render(state); },
        });
      });
      new MutationObserver(() => this.render(state)).observe(select, { childList: true, subtree: true, characterData: true, attributes: true });
      select.addEventListener('change', () => this.render(state));
      select.addEventListener('focus', () => button.focus());
      select.addEventListener('invalid', () => button.classList.add('is-invalid'));

      button.addEventListener('click', () => (list.hidden ? this.show(state) : this.hide(state)));
      button.addEventListener('keydown', (e) => this.onKey(e, state));
      list.addEventListener('mousedown', (e) => e.preventDefault());
      list.addEventListener('click', (e) => {
        // The list can sit inside a <label>: stop the click from re-activating (re-opening) the button.
        e.preventDefault();
        e.stopPropagation();
        const li = e.target.closest('[data-index]');
        if (li && li.getAttribute('aria-disabled') !== 'true') this.choose(state, Number(li.dataset.index));
      });
      this.render(state);
    },

    render(state) {
      const { select, list, value, button } = state;
      list.innerHTML = '';
      Array.from(select.children).forEach((node) => {
        if (node.tagName === 'OPTGROUP') {
          const head = document.createElement('li');
          head.className = 'cselect__group';
          head.setAttribute('role', 'presentation');
          head.textContent = node.label;
          list.append(head);
          Array.from(node.children).forEach((opt) => list.append(this.item(opt, true)));
        } else if (node.tagName === 'OPTION') {
          list.append(this.item(node, false));
        }
      });
      const current = select.options[select.selectedIndex];
      const placeholder = !current || current.value === '';
      value.textContent = current ? current.textContent.trim() : '';
      value.classList.toggle('is-placeholder', placeholder);
      button.disabled = select.disabled;
      if (!placeholder) button.classList.remove('is-invalid');
    },

    item(opt, grouped) {
      const li = document.createElement('li');
      li.className = `cselect__option${grouped ? ' cselect__option--grouped' : ''}`;
      li.setAttribute('role', 'option');
      li.dataset.index = opt.index;
      li.textContent = opt.textContent.trim();
      li.setAttribute('aria-selected', String(opt.selected && opt.value !== ''));
      if (opt.disabled) li.setAttribute('aria-disabled', 'true');
      if (opt.value === '' && !opt.selected) li.hidden = true;
      if (opt.value === '' && opt.disabled) li.hidden = true;
      return li;
    },

    enabledItems(state) {
      return $$('.cselect__option:not([hidden])', state.list).filter((li) => li.getAttribute('aria-disabled') !== 'true');
    },

    show(state) {
      if (this.open && this.open !== state) this.hide(this.open);
      this.open = state;
      state.list.hidden = false;
      state.wrap.classList.add('is-open');
      state.button.setAttribute('aria-expanded', 'true');
      const items = this.enabledItems(state);
      const selected = items.findIndex((li) => li.getAttribute('aria-selected') === 'true');
      this.highlight(state, selected >= 0 ? selected : 0);
      // open upwards when there is no room below
      const rect = state.button.getBoundingClientRect();
      state.wrap.classList.toggle('is-up', window.innerHeight - rect.bottom < 260 && rect.top > 260);
    },

    hide(state) {
      state.list.hidden = true;
      state.wrap.classList.remove('is-open');
      state.button.setAttribute('aria-expanded', 'false');
      if (this.open === state) this.open = null;
    },

    highlight(state, i) {
      const items = this.enabledItems(state);
      items.forEach((li) => li.classList.remove('is-active'));
      if (!items.length) return;
      state.active = Math.max(0, Math.min(items.length - 1, i));
      const li = items[state.active];
      li.classList.add('is-active');
      li.scrollIntoView({ block: 'nearest' });
    },

    choose(state, index) {
      const { select } = state;
      if (select.selectedIndex !== index) {
        select.selectedIndex = index;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.hide(state);
      state.button.focus();
    },

    onKey(e, state) {
      const openNow = !state.list.hidden;
      const items = this.enabledItems(state);
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key) && !openNow) {
        e.preventDefault();
        this.show(state);
        return;
      }
      if (!openNow) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); this.highlight(state, state.active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.highlight(state, state.active - 1); }
      else if (e.key === 'Home') { e.preventDefault(); this.highlight(state, 0); }
      else if (e.key === 'End') { e.preventDefault(); this.highlight(state, items.length - 1); }
      else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const li = items[state.active];
        if (li) this.choose(state, Number(li.dataset.index));
      } else if (e.key === 'Escape' || e.key === 'Tab') { this.hide(state); }
      else if (e.key.length === 1) {
        const start = state.active + 1;
        const k = e.key.toLowerCase();
        const order = items.slice(start).concat(items.slice(0, start));
        const hit = order.find((li) => li.textContent.trim().toLowerCase().startsWith(k));
        if (hit) this.highlight(state, items.indexOf(hit));
      }
    },

    scan(root = document) {
      $$('select.select', root).forEach((s) => this.enhance(s));
    },
  };

  CustomSelect.scan();
  document.addEventListener('click', (e) => {
    if (CustomSelect.open && !CustomSelect.open.wrap.contains(e.target)) CustomSelect.hide(CustomSelect.open);
  });
  // Selects added later (Personnalisation rows, theme editor, cart / quick add refreshes)
  new MutationObserver((records) => {
    records.forEach((r) => r.addedNodes.forEach((n) => {
      if (n.nodeType !== 1) return;
      if (n.matches && n.matches('select.select')) CustomSelect.enhance(n);
      else if (n.querySelector && n.querySelector('select.select')) CustomSelect.scan(n);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* ------------------------------------------------------------------ */
  /* Drawers (mobile menu, cart)                                         */
  /* ------------------------------------------------------------------ */

  const Drawer = {
    open(el, opener) {
      if (!el || el.classList.contains('is-open')) return;
      el._opener = opener || document.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      $$(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      lockScroll();
      const panel = $('.drawer__panel', el);
      requestAnimationFrame(() => panel && panel.focus({ preventScroll: true }));
    },
    close(el) {
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      $$(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      unlockScroll();
      if (el._opener && el._opener.focus) el._opener.focus({ preventScroll: true });
    },
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      Drawer.open(document.getElementById(opener.dataset.drawerOpen), opener);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('[data-drawer]');
      if (drawer) { e.preventDefault(); Drawer.close(drawer); }
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    $$('[data-drawer].is-open').forEach((d) => Drawer.close(d));
    $$('[data-facet][open]').forEach((d) => d.removeAttribute('open'));
    const search = $('#HeaderSearch');
    if (search && !search.hidden) toggleSearch(false);
  });

  /* ------------------------------------------------------------------ */
  /* Header: search, mega menu on touch                                  */
  /* ------------------------------------------------------------------ */

  function toggleSearch(force) {
    const panel = $('#HeaderSearch');
    if (!panel) return;
    const show = typeof force === 'boolean' ? force : panel.hidden;
    panel.hidden = !show;
    $$('[data-search-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(show)));
    if (show) $('input[type="search"]', panel).focus();
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-toggle]')) { e.preventDefault(); toggleSearch(); }
  });

  const isTouch = window.matchMedia('(hover: none)').matches;
  $$('[data-mega]').forEach((item) => {
    const link = $('.main-nav__link', item);
    if (isTouch && link) {
      link.addEventListener('click', (e) => {
        if (!item.classList.contains('is-open')) {
          e.preventDefault();
          $$('[data-mega].is-open').forEach((o) => o !== item && o.classList.remove('is-open'));
          item.classList.add('is-open');
          link.setAttribute('aria-expanded', 'true');
        }
      });
    }
    item.addEventListener('mouseenter', () => link && link.setAttribute('aria-expanded', 'true'));
    item.addEventListener('mouseleave', () => { item.classList.remove('is-open'); link && link.setAttribute('aria-expanded', 'false'); });
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-mega]')) $$('[data-mega].is-open').forEach((o) => o.classList.remove('is-open'));
  });

  /* ------------------------------------------------------------------ */
  /* Quantity input                                                      */
  /* ------------------------------------------------------------------ */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-step]');
        if (!btn) return;
        const step = Number(btn.dataset.qtyStep);
        const min = Number(this.input.min || 0);
        const next = Math.max(min, (Number(this.input.value) || 0) + step);
        if (next === Number(this.input.value)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */

  const Cart = {
    drawer: () => $('cart-drawer'),
    sectionIds() {
      const ids = [];
      const drawer = this.drawer();
      if (drawer) ids.push(drawer.dataset.sectionId);
      const page = $('[data-cart-page]');
      if (page) ids.push(page.dataset.sectionId);
      return ids;
    },
    render(sections) {
      if (!sections) return;
      const drawer = this.drawer();
      if (drawer && sections[drawer.dataset.sectionId]) {
        const doc = parseHTML(sections[drawer.dataset.sectionId]);
        const content = $('[data-cart-content]', doc);
        const title = $('[data-cart-title]', doc);
        if (content) $('[data-cart-content]', drawer).innerHTML = content.innerHTML;
        if (title) $('[data-cart-title]', drawer).innerHTML = title.innerHTML;
      }
      const page = $('[data-cart-page]');
      if (page && sections[page.dataset.sectionId]) {
        const doc = parseHTML(sections[page.dataset.sectionId]);
        const section = page.closest('.shopify-section');
        const fresh = $('.shopify-section', doc) || doc.body;
        if (section) section.innerHTML = fresh.innerHTML;
      }
    },
    updateCount(count, bump = false) {
      $$('[data-cart-count]').forEach((el) => {
        el.dataset.count = count;
        el.textContent = count > 0 ? count : '';
      });
      if (bump) {
        $$('[data-cart-open]').forEach((icon) => {
          icon.classList.remove('is-bumping');
          void icon.offsetWidth; // restart the animation
          icon.classList.add('is-bumping');
        });
      }
    },
    async refresh() {
      const ids = this.sectionIds();
      if (!ids.length) return;
      const res = await fetch(`${routes.root}?sections=${ids.join(',')}`);
      const sections = await res.json();
      this.render(sections);
      const cart = await fetchJSON(`${routes.cart}.js`);
      this.updateCount(cart.item_count);
      return cart;
    },
    async change(key, quantity) {
      const line = $(`[data-line-key="${CSS.escape(key)}"]`);
      line && line.classList.add('is-loading');
      try {
        const cart = await fetchJSON(`${routes.cartChange}.js`, {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity, sections: this.sectionIds() }),
        });
        this.render(cart.sections);
        this.updateCount(cart.item_count);
      } catch (err) {
        line && line.classList.remove('is-loading');
        window.alert(err.message);
      }
    },
    async applyDiscount(codes, attempted) {
      const res = await fetchJSON(`${routes.cartUpdate}.js`, {
        method: 'POST',
        body: JSON.stringify({ discount: codes.join(','), sections: this.sectionIds() }),
      });
      this.render(res.sections);
      if (attempted) {
        const found = (res.discount_codes || []).find((d) => d.code.toUpperCase() === attempted.toUpperCase());
        if (!found || !found.applicable) {
          $$('[data-discount-msg]').forEach((m) => {
            m.hidden = false;
            m.textContent = (theme.strings.discountInvalid || 'Ce code promo n’est pas valable pour ce panier.');
          });
        }
      }
      return res;
    },
    open(notice) {
      const drawer = this.drawer();
      if (!drawer) { window.location.href = routes.cart; return; }
      const noticeEl = $('[data-cart-notice]', drawer);
      if (noticeEl) {
        noticeEl.hidden = !notice;
        const text = $('[data-cart-notice-text]', noticeEl);
        if (text && notice) text.textContent = notice === 'pack' ? text.dataset.pack : text.dataset.default;
      }
      Drawer.open(drawer);
    },
  };
  window.VelesCart = Cart;

  class CartDrawer extends HTMLElement {}
  customElements.define('cart-drawer', CartDrawer);

  // Open cart from header icon (except on the cart page itself)
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (!opener || !Cart.drawer() || $('[data-cart-page]')) return;
    e.preventDefault();
    Cart.open(null);
  });

  // Quantity / remove in cart lines
  document.addEventListener('change', (e) => {
    const input = e.target.closest('input[data-line-key]');
    if (!input) return;
    Cart.change(input.dataset.lineKey, Math.max(0, Number(input.value) || 0));
  });
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-line-remove]');
    if (remove) { e.preventDefault(); Cart.change(remove.dataset.lineRemove, 0); }
  });

  // Discount codes
  const currentCodes = (form) => {
    const field = $('[data-discount-current]', form);
    return field && field.value ? field.value.split(',').filter(Boolean) : [];
  };
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = $('input[name="discount"]', form);
    const code = input.value.trim();
    if (!code) return;
    const btn = $('button[type="submit"]', form);
    btn.classList.add('is-loading');
    try {
      const codes = currentCodes(form).filter((c) => c.toUpperCase() !== code.toUpperCase());
      await Cart.applyDiscount([...codes, code], code);
    } catch (err) {
      const msg = $('[data-discount-msg]', form);
      if (msg) { msg.hidden = false; msg.textContent = err.message; }
    } finally {
      btn.classList.remove('is-loading');
    }
  });
  document.addEventListener('click', async (e) => {
    const tag = e.target.closest('[data-discount-remove]');
    if (!tag) return;
    e.preventDefault();
    const form = tag.closest('[data-discount-form]');
    const codes = currentCodes(form).filter((c) => c !== tag.dataset.discountRemove);
    await Cart.applyDiscount(codes.length ? codes : [''], null);
  });

  /* ------------------------------------------------------------------ */
  /* Product page                                                        */
  /* ------------------------------------------------------------------ */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('.gallery__thumb', this);
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.show(t.dataset.mediaId)));

      const zoomBtn = $('[data-zoom-open]', this);
      const root = this.closest('product-info') || document;
      this.dialog = $('[data-zoom-dialog]', root);
      if (zoomBtn && this.dialog) {
        zoomBtn.addEventListener('click', () => this.zoom());
        $('[data-zoom-close]', this.dialog).addEventListener('click', () => this.dialog.close());
        $('[data-zoom-body]', this.dialog).addEventListener('click', () => this.dialog.close());
      }

      // Swipe on touch screens
      const main = $('.gallery__main', this);
      let startX = null;
      main.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      main.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) < 40) return;
        const idx = this.slides.findIndex((s) => s.classList.contains('is-active'));
        const next = this.slides[(idx + (dx < 0 ? 1 : -1) + this.slides.length) % this.slides.length];
        if (next) this.show(next.dataset.mediaId);
      });
    }

    show(mediaId) {
      if (!mediaId) return;
      const target = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!target) return;
      this.slides.forEach((s) => s.classList.toggle('is-active', s === target));
      this.thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.mediaId === String(mediaId))));
      const thumb = this.thumbs.find((t) => t.dataset.mediaId === String(mediaId));
      if (thumb) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }

    zoom() {
      const active = $('.gallery__slide.is-active img', this);
      if (!active) return;
      const body = $('[data-zoom-body]', this.dialog);
      const img = new Image();
      img.src = (active.currentSrc || active.src).replace(/([?&])width=\d+/, '$1width=3000');
      img.alt = active.alt;
      body.replaceChildren(img);
      this.dialog.showModal();
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', () => this.dispatchEvent(new CustomEvent('variant:select', { bubbles: true })));
    }
    selectedValues(count) {
      const values = [];
      for (let i = 1; i <= count; i += 1) {
        const checked = $(`input[data-option-position="${i}"]:checked`, this);
        const select = $(`select[data-option-position="${i}"]`, this);
        values.push(checked ? checked.value : (select && select.value) || null);
      }
      return values;
    }
  }
  customElements.define('variant-picker', VariantPicker);

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = $('[data-product-json]', this);
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.picker = $('variant-picker', this);
      this.form = $('product-form', this);
      this.gallery = $('media-gallery', this);
      this.addEventListener('variant:select', () => this.onSelect());
      if (this.picker) this.refreshAvailability(this.picker.selectedValues(this.product.options.length));

      // Edit-pack mode: ?edit=<line key>&qty=n
      const params = new URLSearchParams(window.location.search);
      this.editKey = params.get('edit');
      if (this.editKey && this.form) {
        const label = $('[data-add-label]', this.form);
        if (label) label.textContent = strings.updatePack;
        const qty = $('input[name="quantity"]', this.form);
        if (qty && params.get('qty')) qty.value = params.get('qty');
        this.form.editKey = this.editKey;
        // Switching the pack colour keeps editing the same cart line
        $$('[data-sibling-link]', this).forEach((a) => {
          const url = new URL(a.href, window.location.origin);
          url.searchParams.set('edit', this.editKey);
          if (params.get('qty')) url.searchParams.set('qty', params.get('qty'));
          a.href = url.toString();
        });
      }
    }

    onSelect() {
      const count = this.product.options.length;
      const values = this.picker.selectedValues(count);
      const complete = values.every((v) => v !== null && v !== '');
      const match = complete
        ? this.product.variants.find((v) => v.options.every((o, i) => o === values[i]))
        : null;
      const variant = match && match.id ? match : null;

      // Products with more than 250 variants (the packs): Liquid only lists the first 250,
      // so a missing combination is asked to Shopify (?option_values=…) and then cached.
      if (complete && !match && this.isPartial()) {
        this.fetchVariant(values);
        return;
      }

      this.refreshAvailability(values);

      const input = this.form && $('[data-variant-input]', this.form);
      const button = this.form && $('[data-add-button]', this.form);
      const label = this.form && $('[data-add-label]', this.form);
      const addText = this.form && this.form.editKey ? strings.updatePack : strings.addToCart;

      if (input) input.value = variant && variant.id ? variant.id : '';
      if (button && label) {
        if (!complete) { button.disabled = true; label.textContent = strings.chooseOptions; }
        else if (!variant) { button.disabled = true; label.textContent = strings.unavailable; }
        else if (!variant.available) { button.disabled = true; label.textContent = strings.soldOut; }
        else { button.disabled = false; label.textContent = addText; }
      }

      if (variant) {
        const price = $('[data-price]', this);
        if (price) price.innerHTML = variant.price_html;
        if (variant.featured_media && this.gallery) this.gallery.show(variant.featured_media);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    isPartial() {
      return this.product.variants_count > this.product.variants.length;
    }

    async fetchVariant(values) {
      const key = values.join('|');
      if (this.pending === key) return;
      this.pending = key;
      const button = this.form && $('[data-add-button]', this.form);
      if (button) button.disabled = true;
      try {
        const ids = values.map((value, i) => (this.product.option_value_ids[i] || {})[value]);
        if (ids.some((id) => !id)) throw new Error('unknown option value');
        // No JSON Accept header: Shopify would answer with its own product JSON instead of this view.
        const res = await fetch(`${this.product.url}?view=variant-json&option_values=${ids.join(',')}`);
        const found = await res.json();
        if (found && found.options && found.options.every((o, i) => o === values[i])) {
          this.product.variants.push(found);
        } else {
          // Combination that does not exist: remember it so the button shows "unavailable".
          this.product.variants.push({ id: null, options: values, available: false });
        }
      } catch (err) {
        this.product.variants.push({ id: null, options: values, available: false });
      } finally {
        this.pending = null;
        this.onSelect();
      }
    }

    // Grey out size values that have no available variant with the other current selections
    // (skipped when the list is partial: an unknown combination is not a sold-out one).
    refreshAvailability(values) {
      if (!this.picker || this.isPartial()) return;
      $$('.pill input[data-option-position]', this.picker).forEach((input) => {
        const pos = Number(input.dataset.optionPosition) - 1;
        const available = this.product.variants.some((v) => v.available && v.options[pos] === input.value
          && v.options.every((o, i) => i === pos || values[i] === null || values[i] === '' || values[i] === o));
        input.closest('.pill').classList.toggle('is-unavailable', !available);
      });
      $$('select[data-option-position]', this.picker).forEach((select) => {
        const pos = Number(select.dataset.optionPosition) - 1;
        Array.from(select.options).forEach((opt) => {
          if (!opt.value) return;
          const available = this.product.variants.some((v) => v.available && v.options[pos] === opt.value
            && v.options.every((o, i) => i === pos || values[i] === null || values[i] === '' || values[i] === o));
          const base = opt.dataset.label || opt.textContent;
          opt.dataset.label = base;
          opt.textContent = available ? base : `${base} — ${strings.soldOut}`;
        });
      });
    }
  }
  customElements.define('product-info', ProductInfo);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.formEl = $('form', this);
      if (!this.formEl) return;
      this.formEl.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      e.preventDefault();
      const button = $('[data-add-button]', this);
      const errorEl = $('[data-form-error]', this);
      const id = $('[data-variant-input]', this).value;
      if (errorEl) errorEl.hidden = true;
      if (!id) {
        if (errorEl) { errorEl.textContent = strings.chooseOptions; errorEl.hidden = false; }
        return;
      }
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      try {
        const formData = new FormData(this.formEl);
        const sections = Cart.sectionIds();
        if (sections.length) formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);

        const res = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || strings.error);

        let renderedSections = data.sections;
        // Editing an existing pack: remove the previous line once the new one is in the cart
        if (this.editKey && data.key && data.key !== this.editKey) {
          const cart = await fetchJSON(`${routes.cartChange}.js`, {
            method: 'POST',
            body: JSON.stringify({ id: this.editKey, quantity: 0, sections }),
          }).catch(() => null);
          if (cart) renderedSections = cart.sections;
          this.editKey = null;
          const url = new URL(window.location.href);
          url.searchParams.delete('edit');
          url.searchParams.delete('qty');
          window.history.replaceState({}, '', url.toString());
          const label = $('[data-add-label]', this);
          if (label) label.textContent = strings.addToCart;
        } else if (this.editKey) {
          // Same variant: the quantity was added on top — reset it to the chosen quantity
          const qty = Number(formData.get('quantity')) || 1;
          const cart = await fetchJSON(`${routes.cartChange}.js`, {
            method: 'POST',
            body: JSON.stringify({ id: this.editKey, quantity: qty, sections }),
          }).catch(() => null);
          if (cart) renderedSections = cart.sections;
          this.editKey = null;
        }

        Cart.render(renderedSections);
        const cart = await fetchJSON(`${routes.cart}.js`);
        Cart.updateCount(cart.item_count, true);

        const isPack = (this.closest('product-info') || {}).dataset?.templateSuffix === 'pack';
        if (theme.cartAutoOpen !== false) Cart.open(isPack ? 'pack' : 'default');
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  // Quick add from product cards
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-quick-toggle]');
        if (toggle) {
          const sizes = $('[data-quick-sizes]', this);
          sizes.hidden = !sizes.hidden;
          toggle.setAttribute('aria-expanded', String(!sizes.hidden));
          return;
        }
        const add = e.target.closest('[data-quick-add-id]');
        if (add && !add.disabled) this.add(add);
      });
    }

    async add(btn) {
      btn.classList.add('is-loading');
      try {
        const res = await fetchJSON(`${routes.cartAdd}.js`, {
          method: 'POST',
          body: JSON.stringify({ items: [{ id: Number(btn.dataset.quickAddId), quantity: 1 }], sections: Cart.sectionIds() }),
        });
        Cart.render(res.sections);
        const cart = await fetchJSON(`${routes.cart}.js`);
        Cart.updateCount(cart.item_count, true);
        const label = $('[data-quick-label]', this);
        const sizes = $('[data-quick-sizes]', this);
        if (sizes) sizes.hidden = true;
        const main = $('.quick-add__btn', this);
        if (label && main) {
          const original = label.textContent;
          label.textContent = strings.added;
          main.classList.add('is-added');
          window.setTimeout(() => { label.textContent = original; main.classList.remove('is-added'); }, 2000);
        }
        if (theme.quickAddOpensCart) Cart.open('default');
      } catch (err) {
        window.alert(err.message);
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  }
  customElements.define('quick-add', QuickAdd);

  // Size guide dialog
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-size-guide-open]')) {
      const d = $('[data-size-guide-dialog]');
      if (d) { e.preventDefault(); d.showModal(); }
    }
    if (e.target.closest('[data-size-guide-close]')) $('[data-size-guide-dialog]').close();
    const dialog = e.target.closest('dialog.modal');
    if (dialog && e.target === dialog) dialog.close(); // click on backdrop
  });

  /* ------------------------------------------------------------------ */
  /* Collection filters                                                  */
  /* ------------------------------------------------------------------ */

  $$('[data-facets-form]').forEach((form) => {
    const facets = $$('[data-facet]', form);
    facets.forEach((d) => d.addEventListener('toggle', () => {
      if (d.open) facets.forEach((o) => o !== d && o.removeAttribute('open'));
    }));
    form.addEventListener('change', (e) => {
      if (e.target.matches('[data-autosubmit], input[type="checkbox"]')) form.requestSubmit ? form.requestSubmit() : form.submit();
    });
    form.addEventListener('submit', () => {
      // Drop empty price fields to keep URLs clean
      $$('input[type="number"]', form).forEach((i) => { if (!i.value) i.disabled = true; });
    });
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-facet]')) $$('[data-facet][open]').forEach((d) => d.removeAttribute('open'));
  });

  /* ------------------------------------------------------------------ */
  /* Forms: persistence, conditional "Autre" fields, app endpoint         */
  /* ------------------------------------------------------------------ */

  // Keep what the visitor typed if the page reloads after an error; clear after success.
  $$('[data-persist-form]').forEach((wrap) => {
    const key = `veles-form-${wrap.dataset.persistForm}`;
    const form = wrap.closest('form') || wrap;
    const succeeded = form.querySelector('.form-message--success:not([hidden])');
    if (succeeded) { session.remove(key); succeeded.focus && succeeded.focus(); succeeded.scrollIntoView({ block: 'center' }); return; }
    const saved = session.get(key);
    if (saved) {
      $$('input, select, textarea', wrap).forEach((field) => {
        if (!field.name || field.type === 'file' || field.type === 'hidden' || !(field.name in saved)) return;
        if (field.type === 'checkbox' || field.type === 'radio') field.checked = saved[field.name] === field.value || saved[field.name] === true;
        else field.value = saved[field.name];
        field.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    const save = () => {
      const data = {};
      $$('input, select, textarea', wrap).forEach((field) => {
        if (!field.name || field.type === 'file' || field.type === 'hidden') return;
        if (field.type === 'checkbox') data[field.name] = field.checked;
        else if (field.type === 'radio') { if (field.checked) data[field.name] = field.value; }
        else data[field.name] = field.value;
      });
      session.set(key, data);
    };
    wrap.addEventListener('input', save);
    wrap.addEventListener('change', save);
  });

  // "Autre" → show a free text field
  const syncOther = (el) => {
    const target = document.getElementById(el.dataset.otherToggle);
    if (!target) return;
    const show = el.tagName === 'SELECT' ? el.value === el.dataset.otherValue : el.checked;
    target.hidden = !show;
    $$('input, textarea', target).forEach((i) => {
      i.disabled = !show;
      if (!i.hasAttribute('data-optional') && i.type !== 'hidden') i.required = show;
    });
    if (target.querySelector('[data-tag-input]')) TagInput.validate(target.querySelector('[data-tag-input]'));
  };

  // Dropdown with checkboxes; the ticked values go into one field: "MMA, Jiu-jitsu brésilien".
  const MultiChoice = {
    sync(group) {
      const boxes = $$('input[type="checkbox"]', group);
      const values = boxes.filter((b) => b.checked).map((b) => b.value);
      const proxy = $('[data-mselect-proxy]', group);
      if (proxy) {
        proxy.value = values.join(', ');
        proxy.setCustomValidity(proxy.required && !values.length ? group.dataset.requiredMessage || '' : '');
      }
      const label = $('[data-mselect-value]', group);
      if (label) {
        label.textContent = values.length ? values.join(', ') : group.dataset.placeholder;
        label.classList.toggle('is-placeholder', !values.length);
      }
    },
  };

  class MultiSelect extends HTMLElement {
    connectedCallback() {
      this.button = $('[data-mselect-button]', this);
      this.panel = $('[data-mselect-panel]', this);
      this.button.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.panel.hidden) { this.close(); this.button.focus(); } });
      this.onOutside = (e) => { if (!this.contains(e.target)) this.close(); };
      // An empty required list: open the panel instead of the browser bubble on a hidden field.
      const proxy = $('[data-mselect-proxy]', this);
      if (proxy) proxy.addEventListener('invalid', () => { this.open(); this.button.focus(); });
      MultiChoice.sync(this);
    }

    open() {
      this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onOutside);
    }

    close() {
      this.panel.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onOutside);
    }
  }
  customElements.define('multi-select', MultiSelect);
  document.addEventListener('change', (e) => {
    const group = e.target.closest('[data-multi-choice]');
    if (group) MultiChoice.sync(group);
  });

  // Free products as removable tags (Enter or the « Ajouter » button).
  const TagInput = {
    tags(box) { return box._tags || (box._tags = []); },
    render(box) {
      const list = $('[data-tags]', box);
      list.innerHTML = '';
      this.tags(box).forEach((tag, i) => {
        const li = document.createElement('li');
        li.className = 'tag-input__tag';
        const label = document.createElement('span');
        label.textContent = tag;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'tag-input__remove';
        remove.setAttribute('aria-label', `${box.dataset.removeLabel || 'Retirer'} ${tag}`);
        remove.innerHTML = '&times;';
        remove.addEventListener('click', () => {
          this.tags(box).splice(i, 1);
          this.render(box);
          $('[data-tag-field]', box).focus();
        });
        li.append(label, remove);
        list.append(li);
      });
      $('[data-tag-value]', box).value = this.tags(box).join(', ');
      this.validate(box);
    },
    add(box) {
      const field = $('[data-tag-field]', box);
      field.value.split(/[,;]/).map((v) => v.trim()).filter(Boolean).forEach((v) => {
        if (!this.tags(box).some((t) => t.toLowerCase() === v.toLowerCase())) this.tags(box).push(v);
      });
      field.value = '';
      this.render(box);
    },
    validate(box) {
      const field = $('[data-tag-field]', box);
      const active = !field.disabled;
      field.setCustomValidity(active && !this.tags(box).length ? box.dataset.requiredMessage || ' ' : '');
    },
    init(box) {
      const field = $('[data-tag-field]', box);
      field.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this.add(box); }
      });
      field.addEventListener('input', () => {
        if (field.value.trim()) field.setCustomValidity(''); else this.validate(box);
      });
      $('[data-tag-add]', box).addEventListener('click', () => { this.add(box); field.focus(); });
      // Text typed but not added yet still counts when the form is sent.
      const form = box.closest('form');
      if (form) form.addEventListener('submit', () => { if (field.value.trim()) this.add(box); }, true);
      this.render(box);
    },
  };
  $$('[data-tag-input]').forEach((box) => TagInput.init(box));
  $$('[data-other-toggle]').forEach(syncOther);
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-other-toggle]')) syncOther(e.target);
    if (e.target.matches('input[type="radio"]')) {
      $$(`input[type="radio"][name="${CSS.escape(e.target.name)}"][data-other-toggle]`).forEach(syncOther);
    }
  });

  class QuoteForm extends HTMLElement {
    connectedCallback() {
      this.formEl = this.closest('form');
      this.endpoint = this.dataset.endpoint;
      const file = $('input[type="file"]', this);
      if (file) {
        const list = $('[data-dropzone-files]', this);
        const hint = list ? list.textContent : '';
        const zone = file.closest('[data-dropzone]');
        file.addEventListener('change', () => {
          const max = Number(file.dataset.maxMb || 10) * 1024 * 1024;
          const tooBig = Array.from(file.files).find((f) => f.size > max);
          if (tooBig) {
            this.showError(`${tooBig.name} : ${file.dataset.maxMb} Mo maximum.`);
            file.value = '';
          }
          if (list) list.textContent = file.files.length ? Array.from(file.files).map((f) => f.name).join(', ') : hint;
        });
        ['dragenter', 'dragover'].forEach((t) => zone.addEventListener(t, () => zone.classList.add('is-dragover')));
        ['dragleave', 'drop'].forEach((t) => zone.addEventListener(t, () => zone.classList.remove('is-dragover')));
      }
      if (this.endpoint && this.formEl) this.formEl.addEventListener('submit', (e) => this.submitToApp(e));
      else if (this.dataset.uploadVariant && this.formEl) {
        // Capture phase on window: runs before Shopify's anti-spam (hCaptcha) submit handler,
        // which would otherwise send the form before the files are uploaded.
        window.addEventListener('submit', (e) => { if (e.target === this.formEl) this.uploadThenSubmit(e); }, true);
      }
    }

    // Shopify stores files sent as line item properties on its CDN. Each file is added to the cart
    // with the hidden technical product, its URL is kept, and the line is removed straight away.
    async uploadFile(file) {
      const fd = new FormData();
      fd.append('id', this.dataset.uploadVariant);
      fd.append('quantity', '1');
      fd.append('properties[_Fichier]', file, file.name);
      const res = await fetch(`${window.theme.routes.cartAdd}.js`, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
      const raw = await res.text();
      let line = {};
      try { line = JSON.parse(raw); } catch (e) { /* non-JSON error page */ }
      // Always take the technical line back out of the cart, even if something went wrong.
      if (line.key) {
        await fetch(`${window.theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        }).catch(() => {});
      }
      if (!res.ok || !line.properties || !line.properties._Fichier) {
        console.error('[VÊLES] file upload failed', res.status, raw.slice(0, 500));
        throw new Error(`${file.name} (${res.status}${line.description ? ` : ${line.description}` : ''})`);
      }
      return line.properties._Fichier;
    }

    async uploadThenSubmit(e) {
      const input = $('input[type="file"]', this);
      const files = input ? Array.from(input.files) : [];
      if (this.uploaded || !files.length) return; // nothing to upload: normal Shopify submission
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!this.formEl.reportValidity()) return;
      const btn = $('button[type="submit"]', this);
      $('[data-quote-error]', this).hidden = true;
      btn.classList.add('is-loading');
      btn.disabled = true;
      try {
        const links = [];
        for (const file of files) links.push(`${file.name} : ${await this.uploadFile(file)}`);
        $('[data-upload-links]', this).value = links.join('\n');
        this.uploaded = true;
        btn.disabled = false;
        this.formEl.requestSubmit(btn);
      } catch (err) {
        this.showError(`${this.dataset.uploadError || this.dataset.error} — ${err.message}`);
        btn.disabled = false;
      } finally {
        btn.classList.remove('is-loading');
      }
    }

    showError(msg) {
      const el = $('[data-quote-error]', this);
      if (!el) return;
      el.textContent = msg;
      el.hidden = false;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    async submitToApp(e) {
      e.preventDefault();
      if (!this.formEl.reportValidity()) return;
      const btn = $('button[type="submit"]', this);
      const errorEl = $('[data-quote-error]', this);
      errorEl.hidden = true;
      btn.classList.add('is-loading');
      try {
        const res = await fetch(this.endpoint, { method: 'POST', body: new FormData(this.formEl), headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(this.dataset.error);
        const success = $('[data-quote-success]', this);
        $$('.form-grid, button[type="submit"]', this).forEach((el) => { el.hidden = true; });
        success.hidden = false;
        success.scrollIntoView({ block: 'center', behavior: 'smooth' });
        session.remove(`veles-form-${this.dataset.persistForm}`);
      } catch (err) {
        this.showError(this.dataset.error);
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  }
  customElements.define('quote-form', QuoteForm);

  /* ------------------------------------------------------------------ */
  /* Personnalisation: several articles per request + project reference   */
  /* ------------------------------------------------------------------ */

  // Each row: article (only available ones are selectable), its own option, a quantity.
  // The rows are summarised in one field of the e-mail:
  // "1. Rashguard — Manches : Manches longues — Quantité : 2"
  class ArticleBuilder extends HTMLElement {
    connectedCallback() {
      try { this.articles = JSON.parse(this.dataset.articles || '[]'); } catch (e) { this.articles = []; }
      this.list = $('[data-article-list]', this);
      this.template = $('[data-article-template]', this);
      this.value = $('[data-article-value]', this);
      $('[data-article-add]', this).addEventListener('click', () => { this.addRow(); this.sync(); });
      this.addEventListener('change', () => this.sync());
      this.addEventListener('input', () => this.sync());
      if (!this.list.children.length) this.addRow();
      this.sync();
    }

    addRow() {
      const row = this.template.content.firstElementChild.cloneNode(true);
      const type = $('[data-article-type]', row);
      type.addEventListener('change', () => this.fillOptions(row));
      $('[data-article-remove]', row).addEventListener('click', () => {
        if (this.list.children.length > 1) row.remove();
        this.sync();
      });
      this.list.append(row);
      this.fillOptions(row);
    }

    fillOptions(row) {
      const article = this.articles.find((a) => a.title === $('[data-article-type]', row).value);
      const field = $('[data-article-option-field]', row);
      const select = $('[data-article-option]', row);
      const values = article ? article.values.map((v) => v.trim()).filter(Boolean) : [];
      field.hidden = !values.length;
      $('[data-article-option-label]', row).textContent = article ? article.label : '';
      select.innerHTML = '';
      values.forEach((v) => select.append(new Option(v, v)));
      // sizes of this article (kept if the same size still exists)
      const sizeSelect = $('[data-article-size]', row);
      const sizes = article && article.sizes ? article.sizes.map((v) => v.trim()).filter(Boolean) : [];
      const previous = sizeSelect.value;
      $('[data-article-size-field]', row).hidden = !sizes.length;
      sizeSelect.innerHTML = '';
      sizes.forEach((v) => sizeSelect.append(new Option(v, v)));
      if (sizes.includes(previous)) sizeSelect.value = previous;
      else if (sizes.includes('M')) sizeSelect.value = 'M';
    }

    sync() {
      const rows = $$('.article-row', this.list);
      const lines = rows.map((row, i) => {
        $('[data-article-number]', row).textContent = `${this.dataset.itemLabel} ${i + 1}`;
        $('[data-article-remove]', row).hidden = rows.length < 2;
        const type = $('[data-article-type]', row).value;
        const article = this.articles.find((a) => a.title === type);
        const option = $('[data-article-option]', row).value;
        const size = $('[data-article-size-field]', row).hidden ? '' : $('[data-article-size]', row).value;
        const qty = Math.max(1, parseInt($('[data-article-qty]', row).value, 10) || 0);
        const parts = [type];
        if (article && option) parts.push(`${article.label} : ${option}`);
        if (size) parts.push(`${this.dataset.sizeLabel} : ${size}`);
        parts.push(`${this.dataset.qtyLabel} : ${qty}`);
        if (article && article.price) parts.push(`${this.money(article.price)} × ${qty}`);
        return type ? `${i + 1}. ${parts.join(' — ')}` : '';
      }).filter(Boolean);
      const totals = this.totals(rows);
      if (this.value) this.value.value = lines.concat(totals).join('\n');
    }

    // Cart lines for the Personnalisation order: the variant matching each row's choices.
    items() {
      return $$('.article-row', this.list).map((row) => {
        const article = this.articles.find((a) => a.title === $('[data-article-type]', row).value);
        if (!article || !article.variants) return null;
        const option = $('[data-article-option]', row).value;
        const size = $('[data-article-size]', row).value;
        const variant = article.variants.find((v) => (article.choiceIndex < 0 || v.o[article.choiceIndex] === option)
          && (article.sizeIndex < 0 || v.o[article.sizeIndex] === size));
        const quantity = Math.max(1, parseInt($('[data-article-qty]', row).value, 10) || 0);
        return variant ? { id: variant.id, quantity, available: variant.ok, title: article.title } : null;
      });
    }

    money(cents) {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
    }

    // Fixed prices: each top + bottom pair (e.g. rashguard + short) is billed at the « ensemble » price.
    totals(rows) {
      const box = $('[data-article-total]', this);
      const count = { top: 0, bottom: 0 };
      const unit = { top: 0, bottom: 0 };
      const names = { top: '', bottom: '' };
      let subtotal = 0;
      rows.forEach((row) => {
        const article = this.articles.find((a) => a.title === $('[data-article-type]', row).value);
        if (!article || !article.price) return;
        const qty = Math.max(1, parseInt($('[data-article-qty]', row).value, 10) || 0);
        subtotal += article.price * qty;
        if (article.bundle === 'top' || article.bundle === 'bottom') {
          count[article.bundle] += qty;
          unit[article.bundle] = unit[article.bundle] || article.price;
        }
      });
      this.articles.forEach((a) => { if ((a.bundle === 'top' || a.bundle === 'bottom') && !names[a.bundle]) { names[a.bundle] = a.title; unit[a.bundle] = unit[a.bundle] || a.price; } });
      if (!box) return [];
      if (!subtotal) { box.hidden = true; return []; }
      const bundle = Number(this.dataset.bundlePrice) || 0;
      const saving = bundle ? Math.max(0, unit.top + unit.bottom - bundle) : 0;
      const pairs = saving ? Math.min(count.top, count.bottom) : 0;
      const discount = pairs * saving;
      const total = subtotal - discount;
      box.hidden = false;
      box.innerHTML = '';
      const line = (label, value, cls) => {
        const p = document.createElement('p');
        p.className = `article-total__row${cls ? ` article-total__row--${cls}` : ''}`;
        const a = document.createElement('span'); a.textContent = label;
        const b = document.createElement('span'); b.textContent = value;
        p.append(a, b);
        box.append(p);
      };
      const out = [];
      if (discount) {
        line(this.dataset.tSubtotal, this.money(subtotal));
        line(`${this.dataset.tDiscount}${pairs > 1 ? ` × ${pairs}` : ''}`, `−${this.money(discount)}`, 'discount');
        out.push(`${this.dataset.tSubtotal} : ${this.money(subtotal)}`, `${this.dataset.tDiscount} (${pairs}) : −${this.money(discount)}`);
      }
      line(this.dataset.tTotal, this.money(total), 'total');
      out.push(`${this.dataset.tTotal} : ${this.money(total)}`);
      // one piece missing to make a(nother) pair: suggest it
      if (saving && count.top !== count.bottom && this.dataset.tUpsell) {
        const missing = count.top > count.bottom ? 'bottom' : 'top';
        const p = document.createElement('p');
        p.className = 'article-total__upsell';
        p.textContent = this.dataset.tUpsell
          .replace('[article]', names[missing].toLowerCase())
          .replace('[prix]', this.money(bundle))
          .replace('[montant]', this.money(saving));
        box.append(p);
      }
      return out;
    }
  }
  customElements.define('article-builder', ArticleBuilder);

  // Personnalisation page: the chosen articles go straight to the cart (fixed prices), each line
  // carrying the project reference, the description and the logo / inspiration files.
  class PersoOrder extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      this.builder = $('article-builder', this);
      const file = $('input[type="file"]', this);
      if (file) {
        const list = $('[data-dropzone-files]', this);
        const hint = list ? list.textContent : '';
        const zone = file.closest('[data-dropzone]');
        file.addEventListener('change', () => {
          const max = Number(file.dataset.maxMb || 10) * 1024 * 1024;
          const tooBig = Array.from(file.files).find((f) => f.size > max);
          if (tooBig) {
            this.showError(`${tooBig.name} : ${file.dataset.maxMb} Mo maximum.`);
            file.value = '';
          }
          if (list) list.textContent = file.files.length ? Array.from(file.files).map((f) => f.name).join(', ') : hint;
        });
        ['dragenter', 'dragover'].forEach((t) => zone.addEventListener(t, () => zone.classList.add('is-dragover')));
        ['dragleave', 'drop'].forEach((t) => zone.addEventListener(t, () => zone.classList.remove('is-dragover')));
      }
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
    }

    async submit() {
      $('[data-quote-error]', this).hidden = true;
      if (!this.form.reportValidity()) return;
      const items = this.builder.items();
      if (!items.length || items.some((i) => !i)) { this.showError(this.dataset.error); return; }
      const btn = $('button[type="submit"]', this.form);
      btn.classList.add('is-loading');
      btn.disabled = true;
      const ref = ($('[data-project-ref]', this) || {}).value || '';
      const project = $('[data-perso-project]', this).value.trim();
      const files = Array.from(($('input[type="file"]', this) || {}).files || []);
      try {
        // one request per line (multipart, so the files can travel with the first one)
        for (const [n, item] of items.entries()) {
          const fd = new FormData();
          fd.append('id', item.id);
          fd.append('quantity', item.quantity);
          if (ref) fd.append(`properties[${this.dataset.pRef}]`, ref);
          if (project) fd.append(`properties[${this.dataset.pProject}]`, project);
          if (n === 0) files.forEach((f, i) => fd.append(`properties[${this.dataset.pFiles} ${i + 1}]`, f, f.name));
          const res = await fetch(`${window.theme.routes.cartAdd}.js`, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
          if (!res.ok) {
            let msg = '';
            try { msg = (await res.json()).description || ''; } catch (e) { /* not JSON */ }
            throw new Error(msg || res.status);
          }
        }
        if (ref) session.set('veles-project-ref', ref);
        await Cart.refresh();
        Cart.updateCount((await fetchJSON(`${window.theme.routes.cart}.js`)).item_count, true);
        Cart.open();
      } catch (err) {
        this.showError(`${this.dataset.error} — ${err.message}`);
      } finally {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    }

    showError(msg) {
      const el = $('[data-quote-error]', this);
      el.textContent = msg;
      el.hidden = false;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }
  customElements.define('perso-order', PersoOrder);

  // Project reference (e.g. VLS-P-261016-4821): sent with the request so the future order can be
  // tied to it, and shown again on the confirmation after the page reloads.
  $$('[data-project-ref]').forEach((input) => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const random = String(Math.floor(1000 + Math.random() * 9000));
    input.value = `${input.dataset.prefix || 'VLS-P'}-${String(d.getFullYear()).slice(2)}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${random}`;
    const form = input.closest('form');
    if (form) form.addEventListener('submit', () => session.set('veles-project-ref', input.value));
  });
  $$('[data-project-ref-display]').forEach((el) => {
    const ref = session.get('veles-project-ref');
    if (ref) el.textContent = ` Référence de ton projet : ${ref}.`;
  });

  /* ------------------------------------------------------------------ */
  /* Homepage reviews (Judge.me carousel): show which product was reviewed */
  /* ------------------------------------------------------------------ */

  // Judge.me renders its cards later and leaves the product image lazy (data-src only):
  // load the thumbnail and add the product name next to it.
  // Variant tag typed anywhere in the review text in Judge.me: {NOIR}, {BLANC}, {GRIS}…
  const COLOUR_RE = /\s*\{\s*([^{}]{1,30}?)\s*\}\s*/;
  // Removes the {VARIANTE} tag from the review text and returns it (capitalised).
  const extractReviewColour = (scope) => {
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const m = node.nodeValue.match(COLOUR_RE);
      if (m) {
        node.nodeValue = node.nodeValue.replace(COLOUR_RE, ' ').trim();
        const c = m[1].trim();
        return c.charAt(0).toUpperCase() + c.slice(1).toLowerCase();
      }
    }
    return '';
  };

  const variantImage = (productTitle, colour) => {
    const map = (window.theme.variantImages || {})[productTitle];
    return map && colour ? map[colour.toLowerCase()] : null;
  };

  // Product pages: same marker turned into a small badge (with the variant photo) under each review.
  const badgeProductReviews = () => {
    $$('.section--apps .jdgm-widget [class*="review"]:not([data-veles-colour])').forEach((el) => {
      if (el.children.length && el.querySelector('[class*="review"]')) return; // only innermost review blocks
      el.dataset.velesColour = '1';
      const colour = extractReviewColour(el);
      if (!colour) return;
      const badge = document.createElement('span');
      badge.className = 'review-colour-badge';
      const title = document.querySelector('.product .display, .product h1');
      const src = variantImage(title ? title.textContent.trim() : '', colour);
      if (src) {
        const thumb = document.createElement('img');
        thumb.src = src;
        thumb.alt = '';
        badge.append(thumb);
      }
      const text = document.createElement('span');
      text.className = 'review-colour-badge__text';
      if (title) {
        const name = document.createElement('strong');
        name.textContent = title.textContent.trim();
        text.append(name);
      }
      const value = document.createElement('span');
      value.textContent = `Couleur : ${colour}`;
      text.append(value);
      badge.append(text);
      el.after(badge);
    });
  };
  const productWidget = $('.section--apps .jdgm-widget');
  if (productWidget) {
    badgeProductReviews();
    new MutationObserver(badgeProductReviews).observe(productWidget, { childList: true, subtree: true });
  }

  const labelReviewProducts = (root) => {
    $$('.jdgm-carousel-item__product', root).forEach((box) => {
      if (box.dataset.velesLabelled) return;
      const img = $('img', box);
      if (!img) return;
      const src = img.getAttribute('data-src-retina') || img.getAttribute('data-src');
      if (src && !img.getAttribute('src')) img.setAttribute('src', src);
      const name = img.getAttribute('alt');
      // Colour: a {NOIR} / {BLANC} tag typed in the review text in Judge.me.
      const item = box.closest('.jdgm-carousel-item');
      const colour = item ? extractReviewColour(item) : '';
      if (colour) {
        const src = variantImage(name, colour);
        if (src) {
          // Judge.me's lazy loader copies data-src back into src when the card scrolls into
          // view, so every source attribute points to the variant photo, and it is re-applied
          // if anything changes it again.
          const lock = () => {
            ['src', 'data-src', 'data-src-retina'].forEach((a) => { if (img.getAttribute(a) !== src) img.setAttribute(a, src); });
            if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
          };
          lock();
          new MutationObserver(lock).observe(img, { attributes: true, attributeFilter: ['src', 'data-src', 'data-src-retina', 'srcset'] });
        }
      }
      if (name) {
        const label = document.createElement('span');
        label.className = 'review-product-name';
        label.textContent = colour ? `${name} · ${colour}` : name;
        box.append(label);
      }
      box.dataset.velesLabelled = '1';
    });
    ReviewsBand.init(root);
  };

  // Homepage reviews band: the cards are repeated so the row loops endlessly, then it scrolls
  // on its own (« Bande auto-défilante ») and/or with the arrows, a swipe or the trackpad.
  // Settings in the « Avis clients » section: autoplay, speed, pause on hover, arrows.
  const ReviewsBand = {
    init(root) {
      const section = root.closest('[data-reviews]');
      const track = $('.jdgm-carousel__item-container', root);
      const wrapper = track && $('.jdgm-carousel__item-wrapper', track);
      if (!section || !wrapper || wrapper.dataset.velesBand) return;
      const items = $$('.jdgm-carousel-item', wrapper);
      if (!items.length || !track.clientWidth) return;
      wrapper.dataset.velesBand = '1';

      const nav = $('[data-reviews-nav]', section);
      const base = wrapper.scrollWidth + 16; // one set of cards, gap included
      const overflows = wrapper.scrollWidth > track.clientWidth + 4;
      if (nav) nav.hidden = !overflows;
      if (!overflows) return;

      // repeat the set until a full screen of cards always follows it
      let copies = 1;
      while (base * copies < base + track.clientWidth * 1.5 && copies < 8) {
        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          $$('a, button', clone).forEach((el) => el.setAttribute('tabindex', '-1'));
          wrapper.append(clone);
        });
        copies += 1;
      }
      section.classList.add('is-looping');

      // Moved with a GPU transform (sub-pixel, so slow speeds stay perfectly smooth) instead of
      // scrollLeft, which only moves by whole pixels and stutters.
      const autoplay = section.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      const speed = Number(section.dataset.speed) || 35;
      track.scrollLeft = 0;
      let pos = 0;
      let hovered = false;
      let holdUntil = 0; // after a swipe / wheel / arrow, the band waits a moment before moving again
      let tween = null;
      let drag = null;
      let visible = true;
      let last = performance.now();

      const wrap = () => {
        if (pos >= base) pos -= base;
        if (pos < 0) pos += base;
      };
      const render = () => wrapper.style.setProperty('transform', `translate3d(${-pos}px, 0, 0)`, 'important');
      const hold = () => { holdUntil = performance.now() + 2500; };
      const step = () => items[0].getBoundingClientRect().width + 16;
      const go = (dir) => {
        if (tween) pos = tween.to;
        wrap();
        if (dir < 0 && pos < step()) pos += base;
        tween = { from: pos, to: pos + dir * step(), start: performance.now(), duration: 500 };
        hold();
      };
      if (nav) {
        $('[data-reviews-prev]', nav).addEventListener('click', () => go(-1));
        $('[data-reviews-next]', nav).addEventListener('click', () => go(1));
      }
      if (section.dataset.pauseHover === 'true') {
        track.addEventListener('mouseenter', () => { hovered = true; });
        track.addEventListener('mouseleave', () => { hovered = false; });
      }
      // swipe / mouse drag (vertical page scrolling stays native)
      track.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        drag = { x: e.clientX, pos, moved: false, id: e.pointerId };
        tween = null;
      });
      track.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const dx = e.clientX - drag.x;
        if (!drag.moved && Math.abs(dx) > 6) { drag.moved = true; track.setPointerCapture(e.pointerId); section.classList.add('is-dragging'); }
        if (drag.moved) { pos = drag.pos - dx; wrap(); }
      });
      const endDrag = () => {
        if (!drag) return;
        if (drag.moved) { hold(); setTimeout(() => section.classList.remove('is-dragging'), 0); }
        drag = null;
      };
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('click', (e) => { if (section.classList.contains('is-dragging')) { e.preventDefault(); e.stopPropagation(); } }, true);
      // horizontal trackpad / shift+wheel
      track.addEventListener('wheel', (e) => {
        const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
        if (!dx) return;
        e.preventDefault();
        tween = null;
        pos += dx;
        wrap();
        hold();
      }, { passive: false });
      new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }).observe(track);

      const frame = (now) => {
        if (!wrapper.isConnected) return;
        const dt = Math.min(now - last, 100) / 1000;
        last = now;
        if (tween) {
          const t = Math.min((now - tween.start) / tween.duration, 1);
          pos = tween.from + (tween.to - tween.from) * (1 - Math.pow(1 - t, 3));
          if (t === 1) { tween = null; wrap(); }
        } else if (autoplay && visible && !hovered && !drag && now > holdUntil) {
          pos += speed * dt;
          wrap();
        }
        render();
        requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    },
  };

  const initReviews = (scope) => {
    $$('.reviews__app', scope).forEach((app) => {
      labelReviewProducts(app);
      new MutationObserver(() => labelReviewProducts(app)).observe(app, { childList: true, subtree: true });
    });
  };
  initReviews(document);
  document.addEventListener('shopify:section:load', (e) => initReviews(e.target));

  /* ------------------------------------------------------------------ */
  /* Size assistant (inside « Guide des tailles »)                        */
  /* ------------------------------------------------------------------ */

  // Tables come from theme settings: "M | 170-178 | 65-75" (size | height cm | weight kg).
  // The size for the height and the size for the weight are found separately; the larger one
  // wins (a garment must fit both). « Plus souple » goes one size up on the rashguard.
  class SizeAssistant extends HTMLElement {
    connectedCallback() {
      this.available = (this.dataset.sizes || '').split(',').map((s) => s.trim()).filter(Boolean);
      $('[data-size-form]', this).addEventListener('submit', (e) => { e.preventDefault(); this.recommend(); });
    }

    table(kind) {
      return (this.dataset[kind] || '').split(/\n/).map((line) => {
        const [size, h, w] = line.split('|').map((x) => (x || '').trim());
        const range = (r) => (r || '').split('-').map(Number);
        const [hMin, hMax] = range(h);
        const [wMin, wMax] = range(w);
        return { size, hMin, hMax, wMin, wMax };
      }).filter((r) => r.size && !Number.isNaN(r.hMin + r.hMax + r.wMin + r.wMax))
        .filter((r) => !this.available.length || this.available.includes(r.size));
    }

    // Rows whose range contains the value (first and last); outside the table -> clamped and flagged;
    // in a gap between two ranges -> the next size up.
    locate(rows, value, min, max) {
      const hits = rows.map((r, i) => (value >= r[min] && value <= r[max] ? i : -1)).filter((i) => i >= 0);
      if (hits.length) return { index: hits[0], last: hits[hits.length - 1], out: false };
      if (value < rows[0][min]) return { index: 0, last: 0, out: true };
      if (value > rows[rows.length - 1][max]) return { index: rows.length - 1, last: rows.length - 1, out: true };
      const next = Math.max(0, rows.findIndex((r) => r[min] > value));
      return { index: next, last: next, out: false };
    }

    advise(kind, height, weight, fit) {
      const rows = this.table(kind);
      if (!rows.length) return null;
      const h = this.locate(rows, height, 'hMin', 'hMax');
      const w = this.locate(rows, weight, 'wMin', 'wMax');
      const step = kind === 'rashguard' && fit === 'loose' ? 1 : 0;
      const index = Math.min(rows.length - 1, Math.max(h.index, w.index) + step);
      // Overlapping ranges: a bigger size that also contains both measurements.
      const upper = Math.min(rows.length - 1, Math.min(h.last, w.last) >= Math.max(h.index, w.index) ? Math.min(h.last, w.last) + step : index);
      const other = upper !== index ? rows[upper].size : null;
      return { size: rows[index].size, other, out: h.out || w.out };
    }

    recommend() {
      const form = $('[data-size-form]', this);
      const height = Number(form.height.value);
      const weight = Number(form.weight.value);
      const result = $('[data-size-result]', this);
      const note = $('[data-size-note]', this);
      result.hidden = false;
      result.innerHTML = '';
      if (!height || !weight || height < 120 || height > 230 || weight < 30 || weight > 200) {
        const p = document.createElement('p');
        p.className = 'size-assistant__error';
        p.textContent = this.dataset.tInvalid;
        result.append(p);
        note.hidden = true;
        return;
      }
      const fit = form.fit ? form.fit.value : 'tight';
      const kinds = this.dataset.kinds.split(',');
      let out = false;
      kinds.forEach((kind) => {
        const a = this.advise(kind, height, weight, fit);
        if (!a) return;
        out = out || a.out;
        const block = document.createElement('div');
        block.className = 'size-assistant__line';
        if (kinds.length > 1) {
          const label = document.createElement('span');
          label.className = 'size-assistant__kind';
          label.textContent = this.dataset[`t${kind.charAt(0).toUpperCase()}${kind.slice(1)}`];
          block.append(label);
        }
        const p = document.createElement('p');
        p.className = 'size-assistant__advice';
        const [before, after] = this.dataset.tResult.split('[size]');
        const strong = document.createElement('strong');
        strong.textContent = a.size;
        p.append(before, strong, after || '');
        if (kind === 'rashguard') p.append(` ${fit === 'loose' ? this.dataset.tLoose : this.dataset.tTight}`);
        p.append('.');
        block.append(p);
        if (a.other) {
          const alt = document.createElement('p');
          alt.className = 'size-assistant__alt';
          alt.textContent = this.dataset.tBetween.replace('[other]', a.other);
          block.append(alt);
        }
        result.append(block);
      });
      if (out) {
        const warn = document.createElement('p');
        warn.className = 'size-assistant__alt';
        warn.textContent = this.dataset.tOut;
        result.append(warn);
      }
      note.hidden = false;
    }
  }
  customElements.define('size-assistant', SizeAssistant);

  /* ------------------------------------------------------------------ */
  /* Welcome wheel                                                       */
  /* ------------------------------------------------------------------ */

  const WELCOME_KEY = 'veles-welcome';
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const TONES = {
    light: { fill: '#f4f4f5', text: '#0a0a0a' },
    mid: { fill: '#d5d6d8', text: '#0a0a0a' },
    steel: { fill: '#8c8f95', text: '#ffffff' },
    dark: { fill: '#38393b', text: '#ffffff' },
    black: { fill: '#0b0b0b', text: '#ffffff' },
  };

  // The wheel can be played once per browser: localStorage + a 1-year cookie.
  // (Shopify cannot limit a code per IP; codes are limited to one use per customer at checkout.)
  const wheelState = {
    get() {
      const stored = storage.get(WELCOME_KEY);
      if (stored) return stored;
      const m = document.cookie.match(/(?:^|; )veles_wheel=([^;]+)/);
      if (m) { try { return JSON.parse(decodeURIComponent(m[1])); } catch (e) { return null; } }
      return null;
    },
    set(value) {
      storage.set(WELCOME_KEY, value);
      document.cookie = `veles_wheel=${encodeURIComponent(JSON.stringify(value))}; max-age=31536000; path=/; SameSite=Lax`;
    },
  };

  const svgEl = (name, attrs = {}) => {
    const el = document.createElementNS(SVG_NS, name);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    return el;
  };
  const polar = (r, deg) => {
    const rad = (deg * Math.PI) / 180;
    return [200 + r * Math.sin(rad), 200 - r * Math.cos(rad)];
  };

  class WelcomeOffer extends HTMLElement {
    connectedCallback() {
      this.wheel = $('[data-wheel]', this);
      this.disc = $('[data-wheel-disc]', this);
      this.segments = $$('[data-segment]', this).map((el) => ({ ...el.dataset }));
      this.designMode = this.dataset.designMode === 'true';
      this.rotation = 0;
      this.drawWheel();

      $$('[data-welcome-close]', this).forEach((b) => b.addEventListener('click', () => this.dismiss()));
      $('[data-welcome-skip]', this).addEventListener('click', () => this.dismiss());
      $('[data-welcome-form]', this).addEventListener('submit', (e) => { e.preventDefault(); this.spin(); });
      $('[data-welcome-copy]', this).addEventListener('click', () => this.copy());
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.dismiss(); });

      if (this.designMode) {
        document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === this.dataset.sectionId) this.open(); });
        document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === this.dataset.sectionId) this.close(); });
        document.addEventListener('shopify:block:select', (e) => { if (e.detail.sectionId === this.dataset.sectionId) this.open(); });
        return;
      }

      const state = wheelState.get();
      if (state && state.status === 'won') return;
      if (state && state.status === 'dismissed') {
        const days = Number(this.dataset.reshowDays || 7);
        if (Date.now() - state.at < days * 864e5) return;
      }
      window.setTimeout(() => this.open(), Number(this.dataset.delay || 0) * 1000);
    }

    drawWheel() {
      const svg = $('[data-wheel-svg]', this);
      if (!svg) return;
      svg.replaceChildren();
      const n = this.segments.length;
      const seg = 360 / n;
      const R = 178;
      const uid = this.dataset.sectionId.replace(/[^a-z0-9]/gi, '');

      const defs = svgEl('defs');
      defs.innerHTML = `
        <radialGradient id="rim-${uid}" cx="50%" cy="40%" r="60%"><stop offset="0" stop-color="#3d3d3f"/><stop offset="1" stop-color="#050505"/></radialGradient>
        <linearGradient id="silver-${uid}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f6f7f8"/><stop offset=".35" stop-color="#9ea1a6"/><stop offset=".6" stop-color="#eceef0"/><stop offset="1" stop-color="#7b7e83"/></linearGradient>
        <radialGradient id="sheen-${uid}" cx="35%" cy="25%" r="75%"><stop offset="0" stop-color="#fff" stop-opacity=".22"/><stop offset=".55" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".18"/></radialGradient>`;
      svg.appendChild(defs);

      svg.appendChild(svgEl('circle', { cx: 200, cy: 200, r: 199, fill: `url(#rim-${uid})` }));
      svg.appendChild(svgEl('circle', { cx: 200, cy: 200, r: 186, fill: 'none', stroke: `url(#silver-${uid})`, 'stroke-width': 3 }));

      // Rim lights (blink while spinning)
      const lights = svgEl('g');
      const count = Math.max(12, n * 3);
      for (let i = 0; i < count; i += 1) {
        const [x, y] = polar(192.5, (360 / count) * i + seg / 2);
        lights.appendChild(svgEl('circle', { class: 'wheel__light', cx: x, cy: y, r: 3.2, fill: '#f7f7f7' }));
      }
      svg.appendChild(lights);

      const fontSize = n <= 4 ? 46 : n <= 6 ? 36 : n <= 8 ? 28 : 22;
      this.segments.forEach((s, i) => {
        const tone = TONES[s.tone] || TONES.light;
        const a0 = i * seg;
        const a1 = (i + 1) * seg;
        const [x0, y0] = polar(R, a0);
        const [x1, y1] = polar(R, a1);
        svg.appendChild(svgEl('path', {
          d: `M200 200 L${x0} ${y0} A${R} ${R} 0 ${seg > 180 ? 1 : 0} 1 ${x1} ${y1} Z`,
          fill: tone.fill,
        }));

        const center = a0 + seg / 2;
        const g = svgEl('g', { transform: `rotate(${center} 200 200)` });
        if (s.image) {
          const size = n <= 4 ? 78 : n <= 6 ? 62 : 46;
          const cy = 200 - 108;
          const clipId = `clip-${uid}-${i}`;
          const clip = svgEl('clipPath', { id: clipId });
          clip.appendChild(svgEl('circle', { cx: 200, cy, r: size / 2 }));
          defs.appendChild(clip);
          g.appendChild(svgEl('circle', { cx: 200, cy, r: size / 2 + 3, fill: '#fff' }));
          const img = svgEl('image', { x: 200 - size / 2, y: cy - size / 2, width: size, height: size, 'clip-path': `url(#${clipId})`, preserveAspectRatio: 'xMidYMid slice' });
          img.setAttribute('href', s.image);
          g.appendChild(img);
          const t = svgEl('text', { x: 200, y: 200 - 155, 'text-anchor': 'middle', 'dominant-baseline': 'middle', fill: tone.text, 'font-size': Math.round(fontSize * 0.5) });
          t.textContent = s.label;
          g.appendChild(t);
        } else {
          const t = svgEl('text', { x: 200, y: 200 - 118, 'text-anchor': 'middle', 'dominant-baseline': 'middle', fill: tone.text, 'font-size': fontSize });
          t.textContent = s.label;
          g.appendChild(t);
        }
        svg.appendChild(g);
      });

      // Silver separators, sheen and inner shadow ring
      for (let i = 0; i < n; i += 1) {
        const [x, y] = polar(R, i * seg);
        svg.appendChild(svgEl('line', { x1: 200, y1: 200, x2: x, y2: y, stroke: `url(#silver-${uid})`, 'stroke-width': 2.5 }));
      }
      svg.appendChild(svgEl('circle', { cx: 200, cy: 200, r: R, fill: `url(#sheen-${uid})`, 'pointer-events': 'none' }));
      svg.appendChild(svgEl('circle', { cx: 200, cy: 200, r: R, fill: 'none', stroke: 'rgba(0,0,0,.35)', 'stroke-width': 4 }));
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      const btn = $('[data-welcome-spin]', this);
      btn && btn.focus({ preventScroll: true });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
    }

    dismiss() {
      if (!this.won && !this.designMode) wheelState.set({ status: 'dismissed', at: Date.now() });
      this.close();
    }

    pick() {
      const weights = this.segments.map((s) => Math.max(0, Number(s.weight) || 0));
      const total = weights.reduce((a, b) => a + b, 0);
      const rand = new Uint32Array(1);
      (window.crypto || window.msCrypto).getRandomValues(rand);
      if (!total) return rand[0] % this.segments.length;
      let r = (rand[0] / 4294967296) * total;
      for (let i = 0; i < weights.length; i += 1) {
        r -= weights[i];
        if (r < 0) return i;
      }
      return weights.length - 1;
    }

    async subscribe(email) {
      const body = new URLSearchParams({ form_type: 'customer', utf8: '✓', 'contact[email]': email, 'contact[tags]': 'newsletter,roulette-bienvenue' });
      await fetch(`${routes.root}contact`, { method: 'POST', body, headers: { Accept: 'text/html' } }).catch(() => null);
    }

    spin() {
      if (this.spinning) return;
      const emailInput = $('input[name="email"]', this);
      if (emailInput && !emailInput.reportValidity()) return;
      this.spinning = true;
      $('[data-welcome-spin]', this).disabled = true;
      if (emailInput) this.subscribe(emailInput.value);

      const index = this.pick();
      const seg = 360 / this.segments.length;
      const center = index * seg + seg / 2;
      const jitter = (Math.random() - 0.5) * seg * 0.6;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const current = ((this.rotation % 360) + 360) % 360;
      this.rotation += (reduce ? 0 : 360 * 7) + ((((360 - center - current) % 360) + 360) % 360) + jitter;
      this.wheel.classList.add('is-spinning');
      this.disc.style.transform = `rotate(${this.rotation}deg)`;

      let revealed = false;
      const done = () => {
        if (revealed) return;
        revealed = true;
        this.wheel.classList.remove('is-spinning');
        window.setTimeout(() => this.reveal(this.segments[index]), 350);
      };
      if (reduce) { done(); return; }
      const duration = parseFloat(getComputedStyle(this.disc).transitionDuration) * 1000 || 5200;
      this.disc.addEventListener('transitionend', (e) => { if (e.target === this.disc) done(); }, { once: true });
      window.setTimeout(done, duration + 150);
    }

    reveal(seg) {
      this.won = true;
      this.spinning = false;
      const code = seg.code || '';
      if (!this.designMode) wheelState.set({ status: 'won', at: Date.now(), prize: seg.prize, code });

      const isPercent = Number(seg.value) > 0;
      const prizeEl = $('[data-result-prize]', this);
      prizeEl.textContent = isPercent ? `${seg.value} %` : seg.prize;
      prizeEl.classList.toggle('is-gift', !isPercent);
      const sub = $('[data-result-sub]', this);
      sub.textContent = isPercent ? sub.dataset.subPercent : sub.dataset.subGift;
      const imgWrap = $('[data-result-image]', this);
      if (seg.image && !isPercent) {
        const img = new Image();
        img.src = seg.image;
        img.alt = seg.prize;
        imgWrap.replaceChildren(img);
        imgWrap.hidden = false;
      } else {
        imgWrap.hidden = true;
      }
      $('[data-result-code]', this).textContent = code || '—';

      $('[data-step="intro"]', this).hidden = true;
      const step = $('[data-step="result"]', this);
      step.hidden = false;
      $('[data-welcome-copy]', step).focus({ preventScroll: true });
      this.confetti();

      if (this.dataset.autoApply === 'true' && code && !this.designMode) {
        fetch(`${routes.root}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => null);
      }
    }

    async copy() {
      const code = $('[data-result-code]', this).textContent.trim();
      const btn = $('[data-welcome-copy]', this);
      let ok = false;
      try {
        await navigator.clipboard.writeText(code);
        ok = true;
      } catch (e) {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        ta.remove();
      }
      if (!ok) return;
      const text = $('[data-copy-text]', btn);
      const original = text.textContent;
      text.textContent = btn.dataset.copiedLabel || strings.copied;
      btn.classList.add('is-copied');
      window.setTimeout(() => { text.textContent = original; btn.classList.remove('is-copied'); }, 2200);
    }

    // Short black / white / silver confetti burst (~1.6 s)
    confetti() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const canvas = $('[data-confetti]', this);
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const colors = ['#0a0a0a', '#ffffff', '#c0c0c0', '#8a8d91', '#e3e4e6'];
      const box = $('.welcome__box', this).getBoundingClientRect();
      const originX = box.left + box.width / 2;
      const originY = box.top + box.height * 0.3;
      const pieces = Array.from({ length: w < 750 ? 90 : 150 }, () => ({
        x: originX,
        y: originY,
        vx: (Math.random() - 0.5) * 16,
        vy: -Math.random() * 15 - 4,
        size: Math.random() * 7 + 4,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.4,
        color: colors[Math.floor(Math.random() * colors.length)],
      }));
      const duration = 1600;
      const start = performance.now();
      const frame = (now) => {
        const t = now - start;
        ctx.clearRect(0, 0, w, h);
        ctx.globalAlpha = Math.max(0, 1 - Math.max(0, t - duration * 0.6) / (duration * 0.4));
        pieces.forEach((p) => {
          p.vy += 0.42;
          p.vx *= 0.985;
          p.x += p.vx;
          p.y += p.vy;
          p.rot += p.vr;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
          if (p.color === '#ffffff') {
            ctx.strokeStyle = 'rgba(0,0,0,.25)';
            ctx.lineWidth = 0.5;
            ctx.strokeRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
          }
          ctx.restore();
        });
        if (t < duration) requestAnimationFrame(frame);
        else ctx.clearRect(0, 0, w, h);
      };
      requestAnimationFrame(frame);
    }
  }
  customElements.define('welcome-offer', WelcomeOffer);

  /* ------------------------------------------------------------------ */
  /* Fixed header: slimmer once the page is scrolled                      */
  /* ------------------------------------------------------------------ */

  const siteHeader = document.querySelector('[data-header]');
  if (siteHeader) {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        // Hysteresis: the bar changes height, and the browser re-anchors the scroll; a single
        // threshold would make it flip back and forth near the top.
        const y = window.scrollY;
        if (y > 64) siteHeader.classList.add('is-scrolled');
        else if (y < 8) siteHeader.classList.remove('is-scrolled');
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------ */
  /* Leftover technical lines (quote file uploads) never stay in a cart   */
  /* ------------------------------------------------------------------ */

  if (Array.isArray(window.theme.staleCartKeys) && window.theme.staleCartKeys.length) {
    const updates = {};
    window.theme.staleCartKeys.forEach((key) => { updates[key] = 0; });
    fetch(`${window.theme.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    }).then(() => { if (document.querySelector('cart-drawer') && typeof Cart !== 'undefined' && Cart.refresh) Cart.refresh(); }).catch(() => {});
  }

  /* ------------------------------------------------------------------ */
  /* Sticky product columns                                               */
  /* ------------------------------------------------------------------ */

  const StickyColumns = {
    update() {
      const header = document.querySelector('.site-header');
      const offset = (header ? header.getBoundingClientRect().height : 72) + 24;
      document.querySelectorAll('[data-sticky-col]').forEach((col) => {
        // Taller than the screen: stick by its bottom edge instead of its top.
        const top = Math.min(offset, window.innerHeight - col.offsetHeight - 24);
        col.style.setProperty('--sticky-top', `${Math.round(top)}px`);
      });
    },
    init() {
      if (!document.querySelector('[data-sticky-col]')) return;
      let queued = false;
      const schedule = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          this.update();
        });
      };
      window.addEventListener('resize', schedule);
      window.addEventListener('load', schedule);
      // Accordions, images and variant changes change the column heights
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(schedule);
        document.querySelectorAll('[data-sticky-col]').forEach((col) => ro.observe(col));
      }
      this.update();
    }
  };

  StickyColumns.init();
  document.addEventListener('shopify:section:load', () => StickyColumns.init());

  /* ------------------------------------------------------------------ */
  /* Scroll reveal: replays every time an element re-enters the screen   */
  /* ------------------------------------------------------------------ */

  const Reveal = {
    // [selector, effect]: "text" slides in, "media" uncovers, "item" staggers in a grid.
    targets: [
      ['.media-text__media, .promo-banner__media, .person__media, .split-form__media, .collection-hero', 'media'],
      ['.product-grid > *, .icon-columns__item, .accordion, .site-footer__col', 'item'],
      ['.eyebrow, .display, .heading, .lead, .rte, .button, .section-heading, .statement__text, .hero__text, .story-text__quote, .form-grid, .card__info', 'text']
    ],
    excluded: '.product__media-col, product-info form, .drawer, .modal, dialog, [data-no-reveal]',

    init(root = document) {
      const config = window.theme.reveal || {};
      if (!config.enabled || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        document.documentElement.classList.remove('has-reveal');
        return;
      }
      this.repeat = config.repeat !== false;
      if (!this.pending) {
        this.pending = new Set();
        let queued = false;
        const schedule = () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(() => {
            queued = false;
            this.check();
          });
        };
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        window.addEventListener('load', schedule);
      }
      if (!this.observer) {
        this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
          threshold: [0, 0.25, 0.5, 0.75, 1]
        });
      }
      const scope = root.querySelector ? root : document;
      const main = scope.id === 'MainContent' ? scope : scope.querySelector('#MainContent') || scope;
      const footer = document.querySelector('.site-footer');
      [main, footer].filter(Boolean).forEach((area) => {
        this.targets.forEach(([selector, effect]) => {
          area.querySelectorAll(selector).forEach((el) => {
            if (el.dataset.reveal || el.closest(this.excluded)) return;
            if (el.parentElement && el.parentElement.closest('[data-reveal]')) return;
            el.dataset.reveal = effect;
            this.observer.observe(el);
            // Lazy images start at zero height, which leaves the observer stale: re-check once loaded.
            el.querySelectorAll('img').forEach((img) => {
              if (img.complete) return;
              img.addEventListener('load', () => {
                this.observer.unobserve(el);
                this.observer.observe(el);
              }, { once: true });
            });
          });
        });
      });
      if (config.parallax) {
        document.documentElement.classList.add('has-parallax');
        this.initParallax();
      }
    },

    onIntersect(records) {
      // A batch can hold several records for one element: only its latest state counts.
      const latest = new Map();
      records.forEach((entry) => latest.set(entry.target, entry));
      latest.forEach((entry, el) => {
        if (entry.isIntersecting) {
          if (!el.classList.contains('is-revealed')) this.pending.add(el);
          return;
        }
        this.pending.delete(el);
        if (!this.repeat) return;
        // Leaving through the top: next time it comes back down from above.
        el.classList.toggle('reveal-from-top', entry.boundingClientRect.top < 0);
        el.style.setProperty('--reveal-delay', '0ms');
        el.classList.remove('is-revealed');
      });
      this.check();
    },

    // On-screen elements reveal once their top passes 88% of the viewport. They only hide again
    // when fully off-screen, so the slide-in offset can never push them out and make them flicker.
    // Re-run on scroll/resize because late-loading images can move elements without any
    // intersection change.
    check() {
      if (!this.pending.size) return;
      const enterLine = window.innerHeight * 0.88;
      const ready = [...this.pending]
        .map((el) => ({ el, rect: el.getBoundingClientRect() }))
        .filter(({ rect }) => rect.top < enterLine)
        .sort((a, b) => a.rect.top - b.rect.top || a.rect.left - b.rect.left);
      ready.forEach(({ el }, i) => {
        this.pending.delete(el);
        el.style.setProperty('--reveal-delay', `${Math.min(i, 4) * 70}ms`);
        el.classList.add('is-revealed');
        if (!this.repeat) this.observer.unobserve(el);
      });
    },

    initParallax() {
      if (this.parallaxBound) return;
      this.parallaxBound = true;
      let ticking = false;
      const update = () => {
        ticking = false;
        const vh = window.innerHeight;
        document.querySelectorAll('.hero__media img, .image-banner__media img').forEach((img) => {
          const rect = img.parentElement.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > vh) return;
          const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
          img.style.setProperty('--parallax', `${(progress * -2.5).toFixed(2)}%`);
        });
      };
      const onScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    }
  };

  Reveal.init();
  document.addEventListener('shopify:section:load', (e) => Reveal.init(e.target));

  /* ------------------------------------------------------------------ */
  /* Theme editor: keep drawers usable while editing                     */
  /* ------------------------------------------------------------------ */

  document.addEventListener('shopify:section:select', (e) => {
    const drawer = e.target.querySelector('cart-drawer');
    if (drawer) Cart.open(null);
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = e.target.querySelector('cart-drawer');
    if (drawer) Drawer.close(drawer);
  });
})();
